import { useState } from "react";
import { Check, CheckCircle2, Loader2, Save, ShieldCheck } from "lucide-react";
import { ConfirmDialog } from "./ConfirmDialog.tsx";

/**
 * Save / confirm buttons with visible state, shared by the extraction and
 * quality appraisal pages:
 * - "Unsaved changes" whenever the form differs from what was last saved;
 * - "Save draft" becomes a green "Saved" after saving, until the next edit;
 * - the confirm button becomes a green "... confirmed" state; clicking it
 *   again asks whether to mark it as not confirmed.
 */
export function SaveControls({
  dirty, saving, savedThisVisit, confirmed, size = "sm",
  confirmLabel, confirmedLabel, unconfirmBody, icon = "check",
  onSave, onConfirm, onUnconfirm,
}: {
  dirty: boolean;
  saving: "draft" | "confirm" | "unconfirm" | null;
  savedThisVisit: boolean;
  confirmed: boolean;
  size?: "sm" | "md";
  confirmLabel: string;     // "Confirm extraction"
  confirmedLabel: string;   // "Extraction confirmed"
  unconfirmBody: string;
  icon?: "check" | "shield";
  onSave: () => void;
  onConfirm: () => void;
  onUnconfirm: () => void;
}) {
  const [askUnconfirm, setAskUnconfirm] = useState(false);
  const pad = size === "sm" ? "px-3 py-1.5 text-xs" : "px-3 py-1.5 text-sm";
  const ic = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";
  const ConfirmIcon = icon === "shield" ? ShieldCheck : CheckCircle2;
  const showSaved = savedThisVisit && !dirty && saving === null;
  const showConfirmed = confirmed && !dirty;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {dirty && (
        <span className="flex items-center gap-1.5 text-xs font-medium text-amber-700" role="status">
          <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
          Unsaved changes{confirmed ? ": saving will mark this as not confirmed until you confirm again" : ""}
        </span>
      )}
      <button
        type="button"
        onClick={onSave}
        disabled={saving !== null || showSaved}
        className={`flex items-center gap-1.5 border rounded-md ${pad} disabled:cursor-default ${
          showSaved ? "border-green-300 bg-green-50 text-green-700" : "hover:bg-accent disabled:opacity-50"
        }`}
      >
        {saving === "draft" ? <Loader2 className={`${ic} animate-spin`} /> : showSaved ? <Check className={ic} /> : <Save className={ic} />}
        {saving === "draft" ? "Saving" : showSaved ? "Saved" : "Save draft"}
      </button>
      {showConfirmed ? (
        <button
          type="button"
          onClick={() => setAskUnconfirm(true)}
          disabled={saving !== null}
          title="Click to mark as not confirmed"
          className={`flex items-center gap-1.5 rounded-md font-medium ${pad} bg-green-600 text-white hover:bg-green-700 disabled:opacity-50`}
        >
          {saving === "unconfirm" ? <Loader2 className={`${ic} animate-spin`} /> : <ConfirmIcon className={ic} />}
          {confirmedLabel}
        </button>
      ) : (
        <button
          type="button"
          onClick={onConfirm}
          disabled={saving !== null}
          className={`flex items-center gap-1.5 rounded-md font-medium ${pad} bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50`}
        >
          {saving === "confirm" ? <Loader2 className={`${ic} animate-spin`} /> : <ConfirmIcon className={ic} />}
          {confirmLabel}
        </button>
      )}
      <ConfirmDialog
        open={askUnconfirm}
        title="Mark as not confirmed?"
        body={unconfirmBody}
        confirmLabel="Mark as not confirmed"
        cancelLabel="Keep confirmed"
        onCancel={() => setAskUnconfirm(false)}
        onConfirm={() => { setAskUnconfirm(false); onUnconfirm(); }}
      />
    </div>
  );
}
